/** The associations table's state, pure (backend plan 2026-10-07 §A1 "Controlled API"):
 *
 *  - `associationsReducer(rows, action)` — for a host that keeps the rows in local state
 *    (the operator's Defaults section). One action is one step: a flag sets its
 *    dependants with it (`rules.ts` `flagChanges`), Override copies EVERY field.
 *  - `diffAssociations(baseline, rows)` / `describeDiff(diff)` — what a save would change,
 *    as data (the audit payload preview) and in words (the save dialog).
 *  - `effectiveRows` / `limitState` — the 3 check-in / 1 boarding limits over the
 *    categories the scope ends up with (own rows + inherited rows no own row overrides),
 *    and whether the draft made a breach worse than stored (no-worse: a stored breach
 *    only warns; the server applies the same rule).
 *
 *  A host whose rows live elsewhere (the tour editor's draft) maps the same `AssocAction`s
 *  onto its own store instead of calling the reducer. */
import {
  ALL_FLAGS,
  FLAG_BY_KEY,
  formatCounts,
  isFlag,
  isUnlimited,
  NEW_ROW_DEFAULTS,
  type Flag,
} from './flags';
import { flagChanges, limitProblems, MAX_BOARDING, MAX_CHECKIN, type CategoryRow } from './rules';
import type { AssocAction, AssocCategory, AssocRow, InheritedRow } from './types';

/** How a row is named in labels and words: the category's name, else its code, else the
 *  row id. */
export function rowName(row: Pick<AssocRow, 'category' | 'id' | 'key'>): string {
  return (
    row.category?.name || row.category?.code || (row.id !== undefined ? `#${row.id}` : row.key)
  );
}

const flagRow = (row: AssocRow) => ({ flag: (f: string) => isFlag(f) && row.flags[f] });

/** The next free `new-<n>` key. */
export function nextNewKey(rows: readonly AssocRow[]): string {
  let n = 0;
  for (const r of rows) {
    const m = /^new-(\d+)$/.exec(r.key);
    if (m) n = Math.max(n, Number(m[1]));
  }
  return `new-${n + 1}`;
}

const isLive = (r: AssocRow) => r.state !== 'removed';

/** An own row (not removed) already holds this category. */
export function hasCategory(rows: readonly AssocRow[], categoryId: number): boolean {
  return rows.some((r) => isLive(r) && r.category?.id === categoryId);
}

const clampCount = (n: number) =>
  Number.isFinite(n) ? Math.min(Math.max(Math.trunc(n), 0), 32767) : 0;

export function associationsReducer(
  rows: readonly AssocRow[],
  action: AssocAction,
): readonly AssocRow[] {
  const at = (key: string) => rows.findIndex((r) => r.key === key);
  const replace = (i: number, row: AssocRow) => rows.map((r, j) => (j === i ? row : r));
  switch (action.type) {
    case 'setFlag': {
      const i = at(action.key);
      const row = rows[i];
      if (!row || !isLive(row)) return rows;
      const flags = { ...row.flags };
      for (const [f, v] of Object.entries(flagChanges(action.flag, action.to, flagRow(row)))) {
        if (isFlag(f)) flags[f] = v;
      }
      return replace(i, { ...row, flags });
    }
    case 'setCounts': {
      const i = at(action.key);
      const row = rows[i];
      if (!row || !isLive(row)) return rows;
      return replace(i, {
        ...row,
        min_tags: clampCount(action.min_tags),
        max_tags: clampCount(action.max_tags),
      });
    }
    case 'add': {
      if (hasCategory(rows, action.category.id)) return rows;
      return [
        ...rows,
        {
          key: action.key ?? nextNewKey(rows),
          category: action.category,
          min_tags: NEW_ROW_DEFAULTS.min_tags,
          max_tags: NEW_ROW_DEFAULTS.max_tags,
          flags: { ...NEW_ROW_DEFAULTS.flags },
          state: 'new',
        },
      ];
    }
    case 'remove': {
      const i = at(action.key);
      const row = rows[i];
      if (!row || !isLive(row)) return rows;
      if (row.state === 'new') return rows.filter((_, j) => j !== i);
      return replace(i, { ...row, state: 'removed' });
    }
    case 'restore': {
      const i = at(action.key);
      const row = rows[i];
      if (!row || row.state !== 'removed') return rows;
      return replace(i, { ...row, state: 'stored' });
    }
    case 'move': {
      // Past removed rows: they leave the list on save, so swapping with one would move
      // nothing that matters.
      const i = at(action.key);
      if (i < 0 || !isLive(rows[i]!)) return rows;
      let j = i + action.delta;
      while (j >= 0 && j < rows.length && !isLive(rows[j]!)) j += action.delta;
      if (j < 0 || j >= rows.length) return rows;
      const next = [...rows];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    }
    case 'override': {
      const from = action.from;
      if (!from.category || hasCategory(rows, from.category.id)) return rows;
      return [
        ...rows,
        {
          key: action.key ?? nextNewKey(rows),
          category: { ...from.category },
          min_tags: from.min_tags,
          max_tags: from.max_tags,
          flags: { ...from.flags },
          state: 'new',
        },
      ];
    }
    case 'revertRow': {
      const i = at(action.key);
      const row = rows[i];
      if (!row) return rows;
      return replace(i, {
        ...row,
        min_tags: action.to.min_tags,
        max_tags: action.to.max_tags,
        flags: { ...action.to.flags },
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Overrides and the effective set
// ---------------------------------------------------------------------------

/** The inherited rows, each with the key of the own row (not removed) that overrides it
 *  (same category), if any. */
export function withOverrides(
  rows: readonly AssocRow[],
  inherited: readonly InheritedRow[],
): InheritedRow[] {
  const byCategory = new Map<number, string>();
  for (const r of rows) {
    if (isLive(r) && r.category && !byCategory.has(r.category.id)) {
      byCategory.set(r.category.id, r.key);
    }
  }
  return inherited.map((r) => {
    const by = r.category ? byCategory.get(r.category.id) : undefined;
    const out: InheritedRow = { ...r };
    delete out.overriddenBy;
    if (by) out.overriddenBy = by;
    return out;
  });
}

/** The categories the scope ends up with: own rows (not removed), then the inherited rows
 *  no own row overrides — in display order. */
export function effectiveRows(
  rows: readonly AssocRow[],
  inherited: readonly InheritedRow[] = [],
): AssocRow[] {
  return [...rows.filter(isLive), ...withOverrides(rows, inherited).filter((r) => !r.overriddenBy)];
}

const categoryRow = (r: AssocRow): CategoryRow => ({
  category: r.category?.id ?? null,
  checkin: r.flags.checkin,
  boarding: r.flags.boarding,
});

/** The effective set in the rules' shape (`limitLock`, `limitProblems`). */
export const effectiveCategoryRows = (
  rows: readonly AssocRow[],
  inherited: readonly InheritedRow[] = [],
): CategoryRow[] => effectiveRows(rows, inherited).map(categoryRow);

/** The effective set as stored (the baseline's rows + the inherited rows they do not
 *  override) — what the no-worse rule compares with (`limitState`, `limitLock`). */
export const storedCategoryRows = (
  baseline: ReadonlyMap<string, AssocRow>,
  inherited: readonly InheritedRow[] = [],
): CategoryRow[] =>
  effectiveCategoryRows(
    [...baseline.values()].map((r) => ({ ...r, state: 'stored' as const })),
    inherited,
  );

/** Each check-in row's place in the check-in order (1-based, display order of the
 *  effective set). */
export function checkinOrder(
  rows: readonly AssocRow[],
  inherited: readonly InheritedRow[] = [],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of effectiveRows(rows, inherited)) if (r.flags.checkin) out.set(r.key, out.size + 1);
  return out;
}

export interface LimitState {
  /** The limits the effective set breaks, in words (empty: none). */
  problems: string[];
  /** A limit is broken by MORE categories than stored — the edit made it worse (with no
   *  baseline: any breach). */
  worse: boolean;
}

export function limitState(
  rows: readonly AssocRow[],
  inherited: readonly InheritedRow[] = [],
  baseline?: ReadonlyMap<string, AssocRow>,
): LimitState {
  const now = effectiveCategoryRows(rows, inherited);
  const problems = limitProblems(now);
  if (problems.length === 0) return { problems, worse: false };
  if (!baseline) return { problems, worse: true };
  const before = storedCategoryRows(baseline, inherited);
  const count = (xs: CategoryRow[], f: 'checkin' | 'boarding') => xs.filter((x) => x[f]).length;
  const worse = (['checkin', 'boarding'] as const).some((f) => {
    const max = f === 'checkin' ? MAX_CHECKIN : MAX_BOARDING;
    return count(now, f) > max && count(now, f) > count(before, f);
  });
  return { problems, worse };
}

// ---------------------------------------------------------------------------
// Diff
// ---------------------------------------------------------------------------

export type AssocField = Flag | 'min_tags' | 'max_tags';

export interface AssocFieldChange {
  field: AssocField;
  from: boolean | number;
  to: boolean | number;
}

export interface AssocRowChange {
  key: string;
  category: AssocCategory | null;
  fields: AssocFieldChange[];
}

export interface AssocDiff {
  added: AssocRow[];
  removed: AssocRow[];
  changed: AssocRowChange[];
  /** The kept rows are in another order than stored. */
  reordered: boolean;
  /** Added + removed + changed rows, + 1 for a new order. */
  count: number;
}

/** A stored row's fields that differ from its baseline (counts first, then flags in
 *  catalog order). */
export function rowChanges(base: AssocRow, row: AssocRow): AssocFieldChange[] {
  const out: AssocFieldChange[] = [];
  if (base.min_tags !== row.min_tags)
    out.push({ field: 'min_tags', from: base.min_tags, to: row.min_tags });
  if (base.max_tags !== row.max_tags)
    out.push({ field: 'max_tags', from: base.max_tags, to: row.max_tags });
  for (const f of ALL_FLAGS) {
    if (base.flags[f] !== row.flags[f])
      out.push({ field: f, from: base.flags[f], to: row.flags[f] });
  }
  return out;
}

/** The baseline map a host keeps (the stored rows, in stored order). */
export const baselineOf = (rows: readonly AssocRow[]): ReadonlyMap<string, AssocRow> =>
  new Map(rows.map((r) => [r.key, r]));

export function diffAssociations(
  baseline: ReadonlyMap<string, AssocRow>,
  rows: readonly AssocRow[],
): AssocDiff {
  const present = new Set(rows.map((r) => r.key));
  const added = rows.filter((r) => r.state === 'new');
  const removed = [
    ...rows.filter((r) => r.state === 'removed'),
    ...[...baseline.values()].filter((r) => !present.has(r.key)),
  ];
  const changed: AssocRowChange[] = [];
  for (const r of rows) {
    const base = r.state === 'stored' ? baseline.get(r.key) : undefined;
    const fields = base ? rowChanges(base, r) : [];
    if (fields.length) changed.push({ key: r.key, category: r.category, fields });
  }
  const kept = rows.filter((r) => r.state === 'stored' && baseline.has(r.key)).map((r) => r.key);
  const keptSet = new Set(kept);
  const storedOrder = [...baseline.keys()].filter((k) => keptSet.has(k));
  const reordered = kept.some((k, i) => storedOrder[i] !== k);
  return {
    added,
    removed,
    changed,
    reordered,
    count: added.length + removed.length + changed.length + (reordered ? 1 : 0),
  };
}

/** One row's changes in words ("Check-in on, Boarding off, Tags 0–1 → 1–∞"). */
export function describeRowChange(fields: readonly AssocFieldChange[]): string {
  const parts: string[] = [];
  const min = fields.find((f) => f.field === 'min_tags');
  const max = fields.find((f) => f.field === 'max_tags');
  const maxWords = (n: number) => (isUnlimited(n) ? '∞' : String(n));
  if (min && max) {
    parts.push(
      `Tags ${formatCounts(Number(min.from), Number(max.from))} → ${formatCounts(Number(min.to), Number(max.to))}`,
    );
  } else if (min) {
    parts.push(`Min tags ${String(min.from)} → ${String(min.to)}`);
  } else if (max) {
    parts.push(`Max tags ${maxWords(Number(max.from))} → ${maxWords(Number(max.to))}`);
  }
  for (const f of fields) {
    if (isFlag(f.field)) parts.push(`${FLAG_BY_KEY[f.field].short} ${f.to ? 'on' : 'off'}`);
  }
  return parts.join(', ');
}

/** The diff in words, one line per row ("Added MEAL", "Removed BUS", "MEAL: Check-in on,
 *  Boarding off", "Order changed"). */
export function describeDiff(diff: AssocDiff): string[] {
  return [
    ...diff.added.map((r) => `Added ${rowName(r)}`),
    ...diff.removed.map((r) => `Removed ${rowName(r)}`),
    ...diff.changed.map(
      (c) => `${rowName({ category: c.category, key: c.key })}: ${describeRowChange(c.fields)}`,
    ),
    ...(diff.reordered ? ['Order changed'] : []),
  ];
}
